'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@sib/ui';
import { useTabsListIndicator } from './Tabs.utils';

const Tabs = TabsPrimitive.Root;

interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  withIndicator?: boolean;
  indicatorProps?: React.HtmlHTMLAttributes<HTMLDivElement>;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(
  (
    { className, withIndicator = false, indicatorProps, children, ...props },
    ref
  ) => {
    const { tabsListRef, indicatorPosition, indicatorWidth } =
      useTabsListIndicator({
        showIndicator: withIndicator,
        ...(typeof ref === 'object' && ref?.current && { ref }),
      });

    return (
      <TabsPrimitive.List
        ref={tabsListRef}
        className={cn(
          'bg-muted text-muted-foreground relative inline-flex h-9 items-center justify-center rounded-lg p-1',
          className
        )}
        {...props}
      >
        {withIndicator && (
          <div
            {...indicatorProps}
            className={cn(
              'bg-secondary absolute bottom-0 z-10 h-[5px] transition-all',
              indicatorProps?.className
            )}
            style={{
              ...indicatorProps?.style,
              width: indicatorWidth,
              left: indicatorPosition,
            }}
          />
        )}
        {children}
      </TabsPrimitive.List>
    );
  }
);
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  withSeparator?: boolean;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, withSeparator, ...props }, ref) => (
  <React.Fragment>
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'ring-offset-background focus-visible:ring-ring data-[state=active]:bg-background data-[state=active]:text-foreground inline-flex items-center justify-center rounded-md px-3 py-1 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm',
        className
      )}
      {...props}
    />
    {withSeparator && (
      <div className="bg-border mx-2 h-[15px] w-px last:hidden" />
    )}
  </React.Fragment>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
